import { useState, useRef, useEffect, useCallback } from 'react'
import { createChart, ColorType, CandlestickSeries, HistogramSeries, LineSeries, BarSeries, AreaSeries, BaselineSeries } from 'lightweight-charts'
import type { IChartApi } from 'lightweight-charts'
import { ChevronDown, SlidersHorizontal, Settings, Maximize2, Minimize2, Trash2 } from 'lucide-react'
import '../styles/trading-chart.css'

export type MarketCategory = 'crypto' | 'stocks' | 'commodities' | 'forex' | 'indices'
export type ChartLineType = 'Candles' | 'Bars' | 'Area' | 'Heiken Aishi' | 'Baseline' | 'High low' | 'Column'

export interface OhlcData {
    open: number
    high: number
    low: number
    close: number
    change: number
}

export interface TradingChartProps {
    /** Display pair, e.g. 'BTC/USDT' or 'EUR/USD' */
    pair: string
    marketCategory: MarketCategory
    /** Yahoo Finance symbol for non-crypto assets — used as the seed-price lookup key */
    yahooSymbol?: string | null
    /** Baseline/seed price for non-crypto mock candle generation when live data is unavailable */
    seedPrice?: number
    /** Fallback seed for crypto candle generation if the Binance klines call fails */
    currentPrice?: number
    timeframe: string
    timeframeOptions?: string[]
    onTimeframeChange: (tf: string) => void
    lineType?: ChartLineType
    onIndicatorsClick?: () => void
    onSettingsClick?: () => void
    height?: number
    isMobile?: boolean
    brandLabel?: string
    onOhlcChange?: (ohlc: OhlcData) => void
}

const TIMEFRAME_MAP: Record<string, string> = { '1s': '1s', '15m': '15m', '1H': '1h', '4H': '4h', '1D': '1d', '1W': '1w' }

const toBinanceSymbol = (pair: string) => pair.replace('/', '')

const calcSMA = (data: { time: any; close: number }[], period: number) => {
    const out: { time: any; value: number }[] = []
    for (let i = period - 1; i < data.length; i++) {
        let sum = 0
        for (let j = i - period + 1; j <= i; j++) sum += data[j].close
        out.push({ time: data[i].time, value: sum / period })
    }
    return out
}

const calcEMA = (values: number[], period: number) => {
    const k = 2 / (period + 1)
    const out: number[] = [values[0]]
    for (let i = 1; i < values.length; i++) out.push(values[i] * k + out[i - 1] * (1 - k))
    return out
}

const calcMACD = (data: { time: any; close: number }[]) => {
    if (data.length === 0) return []
    const closes = data.map(d => d.close)
    const ema12 = calcEMA(closes, 12)
    const ema26 = calcEMA(closes, 26)
    const dif = closes.map((_, i) => ema12[i] - ema26[i])
    const dea = calcEMA(dif, 9)
    return data.map((d, i) => ({ time: d.time, dif: dif[i], dea: dea[i], hist: (dif[i] - dea[i]) * 2 }))
}

const formatQuotePrice = (price: number) => {
    if (!isFinite(price) || price === 0) return '0.00'
    if (price >= 1000) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    if (price >= 1) return price.toFixed(4)
    return price.toFixed(6)
}

export default function TradingChart({
    pair,
    marketCategory,
    yahooSymbol,
    seedPrice,
    currentPrice,
    timeframe,
    timeframeOptions = ['1s', '15m', '1H', '4H', '1D', '1W'],
    onTimeframeChange,
    lineType = 'Candles',
    onIndicatorsClick,
    onSettingsClick,
    height,
    isMobile = false,
    brandLabel = 'Bicoin',
    onOhlcChange,
}: TradingChartProps) {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const chartRef = useRef<IChartApi | null>(null)
    const candleSeriesRef = useRef<any>(null)
    const volumeSeriesRef = useRef<any>(null)
    const currentPriceRef = useRef(currentPrice || 0)
    currentPriceRef.current = currentPrice || 0
    // Monotonically-incrementing token — guards against React StrictMode's
    // dev-mode double-invoke (mount → cleanup → mount) and any other race
    // where two renderChart() calls are in flight at once. Without this, a
    // stale call whose data fetch resolves late would still call
    // createChart() into the same container as the newer, already-committed
    // chart, leaving two canvases stacked on top of each other.
    const renderTokenRef = useRef(0)
    const resizeHandlerRef = useRef<(() => void) | null>(null)

    const [ohlcData, setOhlcData] = useState<OhlcData>({ open: 0, high: 0, low: 0, close: 0, change: 0 })
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [isLogScale, setIsLogScale] = useState(false)
    const [isAutoScale, setIsAutoScale] = useState(true)
    const [clock, setClock] = useState(() => new Date())

    const emitOhlc = useCallback((next: OhlcData) => {
        setOhlcData(next)
        onOhlcChange?.(next)
    }, [onOhlcChange])

    // ── Footer clock, matches the reference's live HH:MM:SS ────────────────
    useEffect(() => {
        const t = setInterval(() => setClock(new Date()), 1000)
        return () => clearInterval(t)
    }, [])

    // ── Fullscreen state sync ───────────────────────────────────────────────
    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement)
        document.addEventListener('fullscreenchange', handler)
        return () => document.removeEventListener('fullscreenchange', handler)
    }, [])

    const toggleFullscreen = () => {
        if (!wrapperRef.current) return
        if (!document.fullscreenElement) {
            wrapperRef.current.requestFullscreen?.().catch(() => {})
        } else {
            document.exitFullscreen?.().catch(() => {})
        }
    }

    const toggleLogScale = () => {
        setIsLogScale(prev => {
            const next = !prev
            try { chartRef.current?.priceScale('right').applyOptions({ mode: next ? 1 : 0 }) } catch { /* noop */ }
            return next
        })
    }

    const toggleAutoScale = () => {
        setIsAutoScale(prev => {
            const next = !prev
            try { chartRef.current?.priceScale('right').applyOptions({ autoScale: next }) } catch { /* noop */ }
            return next
        })
    }

    const clearChart = () => {
        try { chartRef.current?.timeScale().fitContent() } catch { /* noop */ }
    }

    // ── Build / rebuild the chart whenever symbol, timeframe, or type changes ──
    const renderChart = useCallback(async () => {
        const container = containerRef.current
        if (!container) return
        const myToken = ++renderTokenRef.current

        const binanceSymbol = toBinanceSymbol(pair)
        const interval = TIMEFRAME_MAP[timeframe] || '1d'
        let candleData: any[] = []
        let volumeData: any[] = []
        const isYahooAsset = marketCategory !== 'crypto' && !!yahooSymbol

        if (isYahooAsset) {
            // Mock OHLC for stocks/forex/commodities/indices — Yahoo Finance history is CORS-blocked from browser
            const yahooTfMap: Record<string, { yi: string }> = {
                '1s': { yi: '1m' }, '15m': { yi: '15m' }, '1H': { yi: '60m' }, '4H': { yi: '1d' }, '1D': { yi: '1d' }, '1W': { yi: '1wk' },
            }
            const { yi: yInterval } = yahooTfMap[timeframe] || { yi: '1d' }
            const seed = seedPrice || 100
            const isIntraM = yInterval.includes('m') || yInterval === '60m'
            let last = seed
            const msMap: Record<string, number> = { '1m': 60000, '15m': 900000, '60m': 3600000, '1d': 86400000, '1wk': 604800000 }
            const stepMs = msMap[yInterval] || 86400000
            const now = Date.now()
            for (let i = 0; i < 180; i++) {
                const ts = now - (180 - i) * stepMs
                const time: any = isIntraM ? Math.floor(ts / 1000) : new Date(ts).toISOString().split('T')[0]
                const v = last * 0.012
                const open = last
                const close = +(open + (Math.random() - 0.48) * v).toFixed(4)
                const high = +(Math.max(open, close) + Math.random() * v * 0.35).toFixed(4)
                const low = +(Math.min(open, close) - Math.random() * v * 0.35).toFixed(4)
                candleData.push({ time, open, high, low, close })
                volumeData.push({ time, value: Math.floor(Math.random() * 8000000 + 200000), color: close >= open ? '#10b981' : '#ef4444' })
                last = close
            }
            if (myToken !== renderTokenRef.current) return
            if (candleData.length > 0) {
                const lastC = candleData[candleData.length - 1], prev = candleData.length > 1 ? candleData[candleData.length - 2] : lastC
                emitOhlc({ open: lastC.open, high: lastC.high, low: lastC.low, close: lastC.close, change: prev.close > 0 ? ((lastC.close - prev.close) / prev.close * 100) : 0 })
            }
        } else {
            try {
                const res = await fetch(`https://api.binance.com/api/v3/klines?symbol=${binanceSymbol}&interval=${interval}&limit=180`)
                if (myToken !== renderTokenRef.current) return
                const klines = await res.json()
                if (Array.isArray(klines) && klines.length > 0) {
                    for (const k of klines) {
                        const time = interval.includes('s') || interval.includes('m') || interval.includes('h')
                            ? Math.floor(k[0] / 1000) : new Date(k[0]).toISOString().split('T')[0]
                        const open = parseFloat(k[1]), high = parseFloat(k[2]), low = parseFloat(k[3]), close = parseFloat(k[4]), volume = parseFloat(k[5])
                        candleData.push({ time, open, high, low, close })
                        volumeData.push({ time, value: volume, color: close >= open ? '#10b981' : '#ef4444' })
                    }
                    if (candleData.length > 0) {
                        const lastC = candleData[candleData.length - 1], prev = candleData.length > 1 ? candleData[candleData.length - 2] : lastC
                        emitOhlc({ open: lastC.open, high: lastC.high, low: lastC.low, close: lastC.close, change: prev.close > 0 ? ((lastC.close - prev.close) / prev.close * 100) : 0 })
                    }
                } else {
                    throw new Error('Invalid klines response')
                }
            } catch {
                if (myToken !== renderTokenRef.current) return
                let lastClose = currentPriceRef.current || 65000
                const isIntraday = interval.includes('s') || interval.includes('m') || interval.includes('h')
                const count = 180
                const now = Date.now()
                let intervalMs = 86400000
                if (interval === '1s') intervalMs = 1000
                else if (interval === '15m') intervalMs = 900000
                else if (interval === '1h') intervalMs = 3600000
                else if (interval === '4h') intervalMs = 14400000
                else if (interval === '1w') intervalMs = 604800000
                for (let i = 0; i < count; i++) {
                    const timestamp = now - (count - i) * intervalMs
                    const time: any = isIntraday ? Math.floor(timestamp / 1000) : new Date(timestamp).toISOString().split('T')[0]
                    const volatility = lastClose * 0.015
                    const chg = (Math.random() - 0.48) * volatility
                    const open = lastClose
                    const close = open + chg
                    const high = Math.max(open, close) + Math.random() * volatility * 0.4
                    const low = Math.min(open, close) - Math.random() * volatility * 0.4
                    candleData.push({ time, open: +open.toFixed(2), high: +high.toFixed(2), low: +low.toFixed(2), close: +close.toFixed(2) })
                    volumeData.push({ time, value: Math.random() * 50000 + 5000, color: close >= open ? '#10b981' : '#ef4444' })
                    lastClose = close
                }
                if (candleData.length > 0) {
                    const lastC = candleData[candleData.length - 1], prev = candleData.length > 1 ? candleData[candleData.length - 2] : lastC
                    emitOhlc({ open: lastC.open, high: lastC.high, low: lastC.low, close: lastC.close, change: prev.close > 0 ? ((lastC.close - prev.close) / prev.close * 100) : 0 })
                }
            }
        }

        if (myToken !== renderTokenRef.current) return
        const seen = new Set<any>()
        candleData = candleData.filter(c => { if (seen.has(c.time)) return false; seen.add(c.time); return true })
        volumeData = volumeData.filter((_: any, i: number) => seen.has(candleData[i]?.time) || i < candleData.length)

        // This is the only point where we actually commit to the DOM — remove
        // whatever chart/canvas is currently there (from a previous committed
        // render) right before creating the new one, so two renderChart()
        // calls racing each other can never both leave a canvas behind.
        if (chartRef.current) {
            try { chartRef.current.remove() } catch { /* noop */ }
            chartRef.current = null
            candleSeriesRef.current = null
            volumeSeriesRef.current = null
        }
        container.innerHTML = ''

        let chart: any
        try {
            chart = createChart(container, {
                width: container.clientWidth,
                height: height || (isMobile ? 280 : container.clientHeight || 400),
                layout: {
                    background: { type: ColorType.Solid, color: getComputedStyle(document.documentElement).getPropertyValue('--ui-bg-chart').trim() || '#06060a' },
                    textColor: getComputedStyle(document.documentElement).getPropertyValue('--ui-chart-text').trim() || '#71717A',
                },
                grid: {
                    vertLines: { color: getComputedStyle(document.documentElement).getPropertyValue('--ui-bg-chart-grid').trim() || '#1a1a24' },
                    horzLines: { color: getComputedStyle(document.documentElement).getPropertyValue('--ui-bg-chart-grid').trim() || '#1a1a24' },
                },
                autoSize: !height,
                timeScale: { borderColor: '#1a1a24', timeVisible: true },
                rightPriceScale: { borderColor: '#1a1a24', mode: isLogScale ? 1 : 0, autoScale: isAutoScale },
                crosshair: { mode: 1 },
            })
        } catch (err) {
            console.error('[TradingChart] createChart failed:', err)
            return
        }

        let cs: any
        const lineData = candleData.map((c: any) => ({ time: c.time, value: c.close }))
        try {
            if (lineType === 'Bars') {
                cs = chart.addSeries(BarSeries, { upColor: '#10b981', downColor: '#ef4444' })
                cs.setData(candleData as any)
            } else if (lineType === 'Area') {
                cs = chart.addSeries(AreaSeries, { lineColor: '#2962FF', topColor: 'rgba(41, 98, 255, 0.4)', bottomColor: 'rgba(41, 98, 255, 0.0)', lineWidth: 2 })
                cs.setData(lineData as any)
            } else if (lineType === 'Heiken Aishi') {
                const haCandles: any[] = []
                for (let i = 0; i < candleData.length; i++) {
                    const c = candleData[i]
                    const prevHa = i > 0 ? haCandles[i - 1] : c
                    const haClose = (c.open + c.high + c.low + c.close) / 4
                    const haOpen = (prevHa.open + prevHa.close) / 2
                    const haHigh = Math.max(c.high, haOpen, haClose)
                    const haLow = Math.min(c.low, haOpen, haClose)
                    haCandles.push({ time: c.time, open: +haOpen.toFixed(2), high: +haHigh.toFixed(2), low: +haLow.toFixed(2), close: +haClose.toFixed(2) })
                }
                cs = chart.addSeries(CandlestickSeries, { upColor: '#10b981', downColor: '#ef4444', borderVisible: false, wickUpColor: '#10b981', wickDownColor: '#ef4444' })
                cs.setData(haCandles as any)
            } else if (lineType === 'Baseline') {
                const avgPrice = lineData.length > 0 ? lineData.reduce((s: number, d: any) => s + d.value, 0) / lineData.length : 0
                cs = chart.addSeries(BaselineSeries, {
                    baseValue: { type: 'price', price: avgPrice },
                    topLineColor: '#10b981', topFillColor1: 'rgba(16, 185, 129, 0.2)', topFillColor2: 'rgba(16, 185, 129, 0.0)',
                    bottomLineColor: '#ef4444', bottomFillColor1: 'rgba(239, 68, 68, 0.0)', bottomFillColor2: 'rgba(239, 68, 68, 0.2)',
                    lineWidth: 2,
                })
                cs.setData(lineData as any)
            } else if (lineType === 'High low') {
                cs = chart.addSeries(LineSeries, { color: '#22d3ee', lineWidth: 2 })
                cs.setData(lineData as any)
                const highLine = chart.addSeries(LineSeries, { color: '#10b981', lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: false })
                highLine.setData(candleData.map((c: any) => ({ time: c.time, value: c.high })) as any)
                const lowLine = chart.addSeries(LineSeries, { color: '#ef4444', lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: false })
                lowLine.setData(candleData.map((c: any) => ({ time: c.time, value: c.low })) as any)
            } else if (lineType === 'Column') {
                cs = chart.addSeries(HistogramSeries, { color: '#2962FF' })
                cs.setData(candleData.map((c: any) => ({ time: c.time, value: c.close, color: c.close >= c.open ? '#10b981' : '#ef4444' })) as any)
            } else {
                cs = chart.addSeries(CandlestickSeries, { upColor: '#10b981', downColor: '#ef4444', borderVisible: false, wickUpColor: '#10b981', wickDownColor: '#ef4444' })
                cs.setData(candleData as any)
            }
        } catch (seriesErr) {
            console.error('[TradingChart] series error:', seriesErr)
            try { chart.remove() } catch { /* noop */ }
            return
        }

        candleSeriesRef.current = cs
        if (volumeData.length > 0) {
            const vs = chart.addSeries(HistogramSeries, { priceFormat: { type: 'volume' }, priceScaleId: 'volume' }, 0)
            vs.setData(volumeData as any)
            chart.priceScale('volume').applyOptions({ scaleMargins: { top: 0.7, bottom: 0.25 } })
            volumeSeriesRef.current = vs
        }

        chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.05, bottom: 0.3 } })

        if (candleData.length > 0 && candleData[0].close !== undefined) {
            try {
                const maConfigs: [number, string][] = [[7, '#F0B90B'], [25, '#EC4899'], [99, '#8B5CF6']]
                for (const [period, color] of maConfigs) {
                    const maData = calcSMA(candleData, period)
                    if (maData.length === 0) continue
                    const maSeries = chart.addSeries(LineSeries, { color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false })
                    maSeries.setData(maData as any)
                }
            } catch (e) { console.warn('[TradingChart] MA overlay failed:', e) }

            try {
                const macdData = calcMACD(candleData)
                if (macdData.length > 0) {
                    const MACD_PANE = 1
                    const histSeries = chart.addSeries(HistogramSeries, { priceLineVisible: false, lastValueVisible: false }, MACD_PANE)
                    histSeries.setData(macdData.map(d => ({ time: d.time, value: d.hist, color: d.hist >= 0 ? '#10b981' : '#ef4444' })) as any)
                    const difSeries = chart.addSeries(LineSeries, { color: '#F0B90B', lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false }, MACD_PANE)
                    difSeries.setData(macdData.map(d => ({ time: d.time, value: d.dif })) as any)
                    const deaSeries = chart.addSeries(LineSeries, { color: '#EC4899', lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false }, MACD_PANE)
                    deaSeries.setData(macdData.map(d => ({ time: d.time, value: d.dea })) as any)
                    try {
                        const panes = chart.panes()
                        if (panes[MACD_PANE]) panes[MACD_PANE].setHeight(isMobile ? 70 : 100)
                    } catch { /* older API shape — non-fatal */ }
                }
            } catch (e) { console.warn('[TradingChart] MACD pane failed:', e) }
        }

        chart.timeScale().fitContent()
        if (myToken !== renderTokenRef.current) { chart.remove(); return }
        chartRef.current = chart

        const handleResize = () => { if (container && chartRef.current) chartRef.current.applyOptions({ width: container.clientWidth }) }
        window.addEventListener('resize', handleResize)
        resizeHandlerRef.current = handleResize
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pair, marketCategory, timeframe, lineType, yahooSymbol, seedPrice, height, isMobile])

    useEffect(() => {
        renderChart().catch(err => console.warn('[TradingChart] render error:', err))
        return () => {
            // Invalidate any render still in flight for this component instance
            // so it can never commit a chart after we've moved on.
            renderTokenRef.current++
            if (resizeHandlerRef.current) {
                window.removeEventListener('resize', resizeHandlerRef.current)
                resizeHandlerRef.current = null
            }
            if (chartRef.current) {
                try { chartRef.current.remove() } catch { /* noop */ }
                chartRef.current = null
                candleSeriesRef.current = null
                volumeSeriesRef.current = null
            }
        }
    }, [renderChart])

    // ── Live WebSocket candle updates from Binance (crypto only) ───────────
    useEffect(() => {
        if (marketCategory !== 'crypto') return
        const binanceSymbol = toBinanceSymbol(pair).toLowerCase()
        const interval = TIMEFRAME_MAP[timeframe] || '1d'
        const wsUrl = `wss://stream.binance.com:9443/ws/${binanceSymbol}@kline_${interval}`
        let ws: WebSocket | null = null
        const usesOHLC = lineType === 'Candles' || lineType === 'Bars' || lineType === 'Heiken Aishi'
        try {
            ws = new WebSocket(wsUrl)
            ws.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data)
                    if (msg.k) {
                        const k = msg.k
                        const isIntraday = interval.includes('s') || interval.includes('m') || interval.includes('h')
                        const time = isIntraday ? Math.floor(k.t / 1000) : new Date(k.t).toISOString().split('T')[0]
                        const candle = { time, open: parseFloat(k.o), high: parseFloat(k.h), low: parseFloat(k.l), close: parseFloat(k.c) }
                        const vol = { time, value: parseFloat(k.v), color: candle.close >= candle.open ? '#10b981' : '#ef4444' }
                        const safeUpdate = (ref: React.MutableRefObject<any>, data: any) => {
                            if (!ref.current) return
                            try { ref.current.update(data) } catch { ref.current = null }
                        }
                        if (usesOHLC) safeUpdate(candleSeriesRef, candle)
                        else if (lineType === 'Column') safeUpdate(candleSeriesRef, { time, value: candle.close, color: candle.close >= candle.open ? '#10b981' : '#ef4444' })
                        else safeUpdate(candleSeriesRef, { time, value: candle.close })
                        safeUpdate(volumeSeriesRef, vol)
                        emitOhlc({ open: candle.open, high: candle.high, low: candle.low, close: candle.close, change: candle.open > 0 ? ((candle.close - candle.open) / candle.open * 100) : 0 })
                    }
                } catch { /* ignore parse errors */ }
            }
        } catch { /* WebSocket connection failed, chart stays static */ }
        return () => { if (ws) { ws.close(); ws = null } }
    }, [pair, timeframe, lineType, marketCategory, emitOhlc])

    const displaySymbol = pair.replace('/', '')
    const clockStr = clock.toLocaleTimeString(undefined, { hour12: false })
    const tzOffset = -clock.getTimezoneOffset() / 60
    const tzLabel = `UTC${tzOffset >= 0 ? '+' : ''}${tzOffset}`

    return (
        <div className={`tc-wrapper ${isMobile ? 'tc-mobile' : ''}`} ref={wrapperRef}>
            <div className="tc-toolbar">
                <div className="tc-toolbar-left">
                    {timeframeOptions.map(tf => (
                        <span key={tf} className={`tc-tf ${timeframe === tf ? 'active' : ''}`} onClick={() => onTimeframeChange(tf)}>{tf}</span>
                    ))}
                    <ChevronDown size={13} className="tc-tf-arrow" />
                </div>
                <div className="tc-toolbar-right">
                    <SlidersHorizontal size={16} className="tc-tool-icon" onClick={onIndicatorsClick} />
                    <Settings size={16} className="tc-tool-icon" onClick={onSettingsClick} />
                    <Trash2 size={16} className="tc-tool-icon" onClick={clearChart} />
                    {isFullscreen
                        ? <Minimize2 size={16} className="tc-tool-icon" onClick={toggleFullscreen} />
                        : <Maximize2 size={16} className="tc-tool-icon" onClick={toggleFullscreen} />}
                </div>
            </div>
            <div className="tc-chart-area" style={height ? { height } : undefined}>
                <div className="tc-symbol-label">{displaySymbol} · {timeframe} · {brandLabel}</div>
                <div className={`tc-quote-badge ${ohlcData.change >= 0 ? 'up' : 'down'}`}>
                    {formatQuotePrice(ohlcData.close)} {ohlcData.change >= 0 ? '+' : ''}{ohlcData.change.toFixed(2)}%
                </div>
                <div ref={containerRef} className="tc-canvas" />
                <div className="tc-axis-scale-toggle">
                    <button type="button" className={`tc-scale-btn ${isAutoScale ? 'active' : ''}`} onClick={toggleAutoScale}>A</button>
                    <button type="button" className={`tc-scale-btn ${isLogScale ? 'active' : ''}`} onClick={toggleLogScale}>L</button>
                </div>
            </div>
            <div className="tc-footer">
                <span className="tc-footer-clock">{clockStr} ({tzLabel})</span>
                <div className="tc-footer-toggles">
                    <span className={!isLogScale ? 'active' : ''}>%</span>
                    <span className={isLogScale ? 'active' : ''} onClick={toggleLogScale}>log</span>
                    <span className={isAutoScale ? 'active' : ''} onClick={toggleAutoScale}>auto</span>
                </div>
            </div>
        </div>
    )
}
